import { Link } from "react-router-dom";
import { Target, BookOpen, Exam, CheckCircle, SealCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { getLessonQuiz } from "@/data/quizzes";
import { LEARNING_PATH } from "@/data/learningPath";

function LessonGroup({ path, items, es, t }) {
  const lesson = LEARNING_PATH.find((l) => l.to === path);
  const qs = getLessonQuiz(path, es ? "es" : "en");
  const slug = path.replace(/\//g, "");
  return (
    <div className="bg-[#121214]/80 border border-white/10 rounded-xl p-4 space-y-3" data-testid={`weak-lesson-${slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {lesson && <lesson.Icon weight="fill" className="w-5 h-5 text-[#F5A524] shrink-0" />}
          <p className="text-white font-medium">{lesson ? t(lesson.labelKey) : path}</p>
          <span className="text-xs font-['JetBrains_Mono'] px-2 py-0.5 rounded-md bg-red-500/10 text-red-300" data-testid={`weak-count-${slug}`}>
            {items.length} {es ? (items.length === 1 ? "fallada" : "falladas") : "missed"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link to={path}>
            <Button size="sm" variant="outline" className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid={`weak-review-${slug}`}>
              <BookOpen className="w-4 h-4 mr-1.5" />
              {es ? "Repasar lección" : "Review lesson"}
            </Button>
          </Link>
          <Link to={`${path}#lesson-quiz`}>
            <Button size="sm" className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041]" data-testid={`weak-retake-${slug}`}>
              <Exam weight="fill" className="w-4 h-4 mr-1.5" />
              {es ? "Hacer su cuestionario" : "Retake its quiz"}
            </Button>
          </Link>
        </div>
      </div>
      <ul className="space-y-2">
        {items.map((i) => {
          const q = qs[i];
          return (
            <li key={i} className="bg-[#0A0A0A] border border-white/5 rounded-lg px-3 py-2.5" data-testid={`weak-q-${slug}-${i}`}>
              <p className="text-white text-sm">{q.q}</p>
              <p className="flex items-start gap-1.5 text-emerald-300 text-sm mt-1.5">
                <CheckCircle weight="fill" className="w-4 h-4 mt-0.5 shrink-0" />
                {q.o[q.a]}
              </p>
              <p className="text-[#A1A1AA] text-xs mt-1 pl-5">{q.w}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function WeakSpots({ missed, score, total }) {
  const { t, lang } = useTranslation();
  const es = lang === "es";
  const groups = Object.entries(
    missed.reduce((acc, m) => ({ ...acc, [m.path]: [...(acc[m.path] || []), m.i] }), {})
  ).sort((a, b) => b[1].length - a[1].length);

  return (
    <section className="space-y-3" data-testid="weak-spots">
      <div>
        <h2 className="flex items-center gap-2 font-['Outfit'] text-white text-xl font-semibold">
          <Target weight="fill" className="w-5 h-5 text-[#F5A524]" />
          {es ? "Tus puntos débiles" : "Your weak spots"}
        </h2>
        <p className="text-[#71717A] text-sm">
          {es
            ? `De tu último examen (${score}/${total}): lecciones de donde vinieron las preguntas falladas, con la respuesta correcta.`
            : `From your latest exam (${score}/${total}): the lessons your missed questions came from, with the right answers.`}
        </p>
      </div>
      {groups.length === 0 ? (
        <div className="flex items-center gap-2 bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4 text-emerald-300" data-testid="weak-spots-none">
          <SealCheck weight="fill" className="w-5 h-5" />
          {es ? "¡Sin puntos débiles! Respondiste todo correctamente." : "No weak spots — you got every question right!"}
        </div>
      ) : (
        groups.map(([path, items]) => <LessonGroup key={path} path={path} items={items} es={es} t={t} />)
      )}
    </section>
  );
}
